import React, { useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { Modal } from '@/feature/alerts/components/Modal';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { renderPredictionTerms } from './components/PredictionTermsContent';
import { ProductTncCheckbox } from './components/ProductTncCheckbox';
import { TermsContentRenderer } from './utils/terms-and-conditions-utils';

const modalId = 'termsAndConditions';

interface ShowTermsAndConditionsOptions {
    onSuccess?: () => void;
    onCancel?: () => void;
    onWebViewOpened?: () => void;
    renderContent?: TermsContentRenderer;
}

interface ModalState {
    isVisible: boolean;
    hasAcceptedTerms: boolean;
    webViewOpened: boolean;
    userAccepted: boolean;
    options?: ShowTermsAndConditionsOptions;
}

export type TermsAndConditionsModalRef = {
    show: (options?: ShowTermsAndConditionsOptions) => void;
};

/**
 * Modal used to show terms and conditions prompt for the active product
 */
export const TermsAndConditionsModal = React.forwardRef<TermsAndConditionsModalRef, {}>((_, ref) => {
    const { t } = useTranslation(['promohub', 'common']);
    const product = useJurisdictionStore(state => state.product);
    const modalRef = useRef<BottomSheetModal>(null);
    const [modalState, setModalState] = useState<ModalState>({
        isVisible: false,
        hasAcceptedTerms: false,
        webViewOpened: false,
        userAccepted: false,
    });

    useImperativeHandle(ref, () => ({
        show: (options?: ShowTermsAndConditionsOptions) => {
            setModalState(prev => ({
                isVisible: true,
                hasAcceptedTerms: prev.webViewOpened ? prev.hasAcceptedTerms : false,
                webViewOpened: false,
                userAccepted: false,
                options,
            }));
            modalRef.current?.present();
        },
    }));

    const handleDismiss = () => {
        if (!modalState.userAccepted && !modalState.webViewOpened && modalState.options?.onCancel) {
            modalState.options.onCancel();
        }
        setModalState(prev => ({ ...prev, isVisible: false }));
    };

    const handleAcceptPress = () => {
        setModalState(prev => ({ ...prev, userAccepted: true }));
        modalState.options?.onSuccess?.();
        modalRef.current?.dismiss();
    };

    const handleWebViewOpened = () => {
        setModalState(prev => ({ ...prev, webViewOpened: true }));
        modalState.options?.onWebViewOpened?.();
    };

    const handleCheckboxToggle = (checked: boolean) => {
        setModalState(prev => ({ ...prev, hasAcceptedTerms: checked }));
    };

    const handleToggle = () => {
        handleCheckboxToggle(!modalState.hasAcceptedTerms);
    };

    const handlePress = () => {
        if (modalState.hasAcceptedTerms) {
            handleAcceptPress();
        } else {
            modalState.options?.onCancel?.();
        }
    };

    const renderContent =
        modalState.options?.renderContent ?? (product === Product.Prediction ? renderPredictionTerms : undefined);

    return (
        <Modal modalRef={modalRef} id={modalId} onDismiss={handleDismiss}>
            {renderContent ? (
                renderContent({
                    hasAcceptedTerms: modalState.hasAcceptedTerms,
                    handleToggle,
                    handleAcceptPress,
                    handleWebViewOpened,
                    handlePress,
                })
            ) : (
                <>
                    <Text variant={'titleMedium'} textAlign={'center'} lineHeight={48}>
                        {t('terms_and_conditions')}
                    </Text>
                    <Box px="s16">
                        <ProductTncCheckbox
                            value={modalState.hasAcceptedTerms}
                            toggleValue={handleToggle}
                            onWebViewOpened={handleWebViewOpened}
                        />
                    </Box>
                    <SizedBox value={16} />
                    <Box mx="s24">
                        <Button
                            label={t('common:confirm_and_continue')}
                            disabled={!modalState.hasAcceptedTerms}
                            hierarchy="primaryLilac"
                            onPress={handlePress}
                        />
                    </Box>
                </>
            )}
        </Modal>
    );
});
